import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, getStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState } from "@/components/common/States";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  Trophy, Target, ArrowDownRight, TrendingUp, TrendingDown, Minus,
  AlertTriangle, ShieldAlert, Pencil, Plus, Trash2, CheckCircle2,
  Clock, AlertCircle, XCircle, CircleDot, Sparkles, CalendarDays,
  Quote, ListTodo, Flag, User, Calendar as CalendarIcon, Check,
  FileDown, Wand2, Lock, X,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";


const SCENARIO_META = {
  best_case: {
    label: "Best Case",
    subtitle: "Ceiling outcome",
    icon: Trophy,
    accent: "from-emerald-400/20 to-emerald-300/0",
    ring: "ring-emerald-300/30",
    text: "text-emerald-200",
    pill: "emerald",
  },
  expected: {
    label: "Expected Case",
    subtitle: "Realistic outcome",
    icon: Target,
    accent: "from-cyan-400/20 to-cyan-300/0",
    ring: "ring-cyan-300/30",
    text: "text-cyan-200",
    pill: "cyan",
  },
  risk: {
    label: "Risk Case",
    subtitle: "Floor outcome",
    icon: ArrowDownRight,
    accent: "from-amber-400/20 to-amber-300/0",
    ring: "ring-amber-300/30",
    text: "text-amber-200",
    pill: "amber",
  },
};

const TIMELINE_STATUS = {
  not_started: { label: "Not Started", tone: "neutral", icon: CircleDot, color: "text-white/40" },
  in_progress: { label: "In Progress", tone: "cyan", icon: Clock, color: "text-cyan-300" },
  completed: { label: "Completed", tone: "emerald", icon: CheckCircle2, color: "text-emerald-300" },
  at_risk: { label: "At Risk", tone: "amber", icon: AlertCircle, color: "text-amber-300" },
  missed: { label: "Missed", tone: "rose", icon: XCircle, color: "text-rose-300" },
};

const SEVERITY_META = {
  low: { tone: "ice", label: "Low", glow: "rgba(34,211,238,0.18)" },
  medium: { tone: "amber", label: "Medium", glow: "rgba(251,191,36,0.22)" },
  high: { tone: "rose", label: "High", glow: "rgba(244,114,182,0.25)" },
};

const RISK_STATUS = {
  open: { tone: "rose", label: "Open" },
  monitoring: { tone: "amber", label: "Monitoring" },
  mitigated: { tone: "cyan", label: "Mitigated" },
  closed: { tone: "emerald", label: "Closed" },
};

const RISK_CATEGORY_LABELS = {
  medical: "Medical",
  athletic_testing: "Athletic Testing",
  production: "Production",
  character: "Character / Maturity",
  positional_projection: "Positional Projection",
  size: "Size Limitations",
  scheme: "Scheme Limitation",
  competition_level: "Competition Level",
  durability: "Durability",
  interview: "Interview Concerns",
};

const OBJECTIVE_CATEGORY_LABELS = {
  testing: "Testing",
  film: "Film",
  interview: "Interview",
  medical: "Medical",
  character: "Character",
  position_projection: "Position Projection",
  special_teams: "Special Teams",
  agent_family_education: "Agent / Family Education",
  nutrition_body_comp: "Nutrition / Body Comp",
  recovery: "Recovery",
};

const OBJECTIVE_PRIORITY_META = {
  high: { tone: "rose", label: "High" },
  medium: { tone: "amber", label: "Medium" },
  low: { tone: "ice", label: "Low" },
};

const OBJECTIVE_STATUS_META = {
  not_started: { tone: "neutral", label: "Not Started", color: "text-white/40" },
  in_progress: { tone: "cyan", label: "In Progress", color: "text-cyan-300" },
  blocked: { tone: "rose", label: "Blocked", color: "text-rose-300" },
  complete: { tone: "emerald", label: "Complete", color: "text-emerald-300" },
};

const WEEKLY_STATUS_META = {
  not_started: { tone: "neutral", label: "Not Started" },
  in_progress: { tone: "cyan", label: "In Progress" },
  complete: { tone: "emerald", label: "Complete" },
};

function useResolvedPlayerId() {
  const { user } = useAuth();
  const { playerId } = useParams();
  return playerId || user?.player_id;
}

function trendIcon(trend) {
  if (trend === "up") return TrendingUp;
  if (trend === "down") return TrendingDown;
  return Minus;
}

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
}

// ============================================================
// Positioning Summary (Hero)
// ============================================================
function PositioningHero({ player, plan, scenarioByKey, isAdmin, onEditSummary }) {
  const TrendIcon = trendIcon(player?.projection_trend);
  const trendTone = player?.projection_trend === "up" ? "emerald" : player?.projection_trend === "down" ? "rose" : "ice";
  const lastUpdated = player?.projection_updated_at || plan?.updated_at;

  return (
    <GlassCard className="col-span-12 p-6 relative overflow-hidden" testId="dp-positioning-hero">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.18), transparent 70%)" }} />
      <div className="grid lg:grid-cols-12 gap-6 relative">
        {/* Left: projection + confidence + trend */}
        <div className="lg:col-span-4">
          <div className="text-3 text-[10px] uppercase tracking-[0.18em]">Current Draft Projection</div>
          <div className="mt-2 text-1 text-3xl font-semibold leading-tight">
            {player?.projected_round || "Unprojected"}
          </div>
          {player?.projected_pick_range && (
            <div className="text-2 text-sm mt-0.5 font-mono">{player.projected_pick_range}</div>
          )}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <StatusPill tone={trendTone} className="gap-1.5">
              <TrendIcon className="h-3 w-3" />
              <span className="capitalize">{player?.projection_trend || "stable"}</span>
            </StatusPill>
            {player?.confidence != null && (
              <div className="flex items-center gap-2">
                <div className="text-3 text-[10px] uppercase tracking-wider">Confidence</div>
                <div className="text-1 text-sm font-mono">{player.confidence}%</div>
                <div className="h-1.5 w-20 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-300 rounded-full" style={{ width: `${player.confidence}%` }} />
                </div>
              </div>
            )}
          </div>
          <div className="mt-3 text-3 text-[11px] flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3" /> Last updated {formatDate(lastUpdated)}
          </div>
        </div>

        {/* Center: 3 outcome headlines */}
        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {["best_case", "expected", "risk"].map((k) => {
            const meta = SCENARIO_META[k];
            const s = scenarioByKey[k] || {};
            const Icon = meta.icon;
            return (
              <div key={k} className={`glass p-3 ring-1 ${meta.ring}`} data-testid={`dp-outcome-${k}`}>
                <div className={`flex items-center gap-1.5 ${meta.text}`}>
                  <Icon className="h-3.5 w-3.5" />
                  <div className="text-[10px] uppercase tracking-[0.18em]">{meta.label}</div>
                </div>
                <div className="text-1 text-sm font-semibold mt-1.5">{s.projected_range || <span className="text-3 italic font-normal">Not set</span>}</div>
                <div className="text-3 text-[11px] mt-0.5">{meta.subtitle}</div>
              </div>
            );
          })}
        </div>

        {/* Right: advisor summary + AI explanation */}
        <div className="lg:col-span-3 flex flex-col gap-3">
          <div className="glass p-3">
            <div className="flex items-center justify-between">
              <div className="text-3 text-[10px] uppercase tracking-wider flex items-center gap-1.5"><Quote className="h-3 w-3" /> Advisor Summary</div>
              {isAdmin && (
                <button onClick={() => onEditSummary("advisor_summary_md")} className="text-2 hover:text-white" data-testid="dp-edit-advisor-summary">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="markdown-body markdown-compact mt-2 text-2 text-[12px] leading-relaxed">
              {plan?.advisor_summary_md
                ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{plan.advisor_summary_md}</ReactMarkdown>
                : <span className="text-3 italic">No advisor summary yet.</span>}
            </div>
          </div>
          <div className="glass p-3">
            <div className="flex items-center justify-between">
              <div className="text-3 text-[10px] uppercase tracking-wider flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI Explanation</div>
              {isAdmin && (
                <button onClick={() => onEditSummary("ai_explanation_md")} className="text-2 hover:text-white" data-testid="dp-edit-ai-explanation">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="markdown-body markdown-compact mt-2 text-2 text-[12px] leading-relaxed">
              {plan?.ai_explanation_md
                ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{plan.ai_explanation_md}</ReactMarkdown>
                : <span className="text-3 italic">No AI explanation yet.</span>}
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

// ============================================================
// Scenario Planning (3-column)
// ============================================================
function ScenarioBoard({ scenarioByKey, isAdmin, onEdit }) {
  return (
    <GlassCard className="col-span-12 p-6" testId="dp-scenario-board">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h3 className="text-1 font-semibold">Draft Scenario Planning</h3>
          <div className="text-3 text-xs mt-0.5">Best Case · Expected Case · Risk Case — required conditions and response plans for each.</div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {["best_case", "expected", "risk"].map((k) => {
          const meta = SCENARIO_META[k];
          const s = scenarioByKey[k] || { key: k };
          const Icon = meta.icon;
          return (
            <div key={k} className={`glass p-5 ring-1 ${meta.ring} relative overflow-hidden flex flex-col`} data-testid={`dp-scenario-${k}`}>
              <div className={`absolute -top-20 -right-20 h-56 w-56 rounded-full pointer-events-none bg-gradient-to-br ${meta.accent}`} />
              <div className="relative flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className={`h-9 w-9 rounded-lg bg-white/5 ring-1 ${meta.ring} grid place-items-center ${meta.text}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className={`text-[10px] uppercase tracking-[0.18em] ${meta.text}`}>{meta.label}</div>
                    <div className="text-3 text-[11px]">{meta.subtitle}</div>
                  </div>
                </div>
                {isAdmin && (
                  <button onClick={() => onEdit(k, s)} className="text-2 hover:text-white" data-testid={`dp-edit-scenario-${k}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="relative mt-4">
                <div className="text-3 text-[10px] uppercase tracking-wider">Projected Range</div>
                <div className="text-1 text-xl font-semibold mt-1">{s.projected_range || <span className="text-3 italic font-normal text-base">Not set</span>}</div>
              </div>

              <div className="relative mt-4 space-y-3 grow">
                <ScenarioField label="What must happen" value={s.required} />
                <ScenarioField label="What could hurt this" value={s.concerns} />
                <ScenarioField label="Response plan" value={s.response_plan} />
                {s.advisor_note && <ScenarioField label="Advisor note" value={s.advisor_note} accent />}
              </div>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

function ScenarioField({ label, value, accent = false }) {
  return (
    <div className={accent ? "glass p-2.5" : ""}>
      <div className={`text-[10px] uppercase tracking-wider ${accent ? "text-cyan-200" : "text-3"}`}>{label}</div>
      <div className="text-2 text-[13px] leading-relaxed mt-1">
        {value ? value : <span className="text-3 italic">—</span>}
      </div>
    </div>
  );
}

// ============================================================
// Timeline
// ============================================================
function Timeline({ events, isAdmin, onEdit }) {
  return (
    <GlassCard className="col-span-12 p-6" testId="dp-timeline-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-1 font-semibold">Draft Timeline</h3>
          <div className="text-3 text-xs mt-0.5">10-stage roadmap from training start through post-draft transition.</div>
        </div>
      </div>

      {/* Single horizontally-scrolling timeline. On large screens it fits the grid;
          on small screens it scrolls with snap points. */}
      <div className="relative -mx-2 px-2 overflow-x-auto scrollbar-thin">
        <div className="absolute left-0 right-0 top-[42px] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        <div
          className="grid relative gap-3 pb-2 min-w-[920px] lg:min-w-0"
          style={{ gridTemplateColumns: `repeat(${events.length || 10}, minmax(0, 1fr))` }}
        >
          {events.map((ev) => (
            <TimelineNode key={ev.id} ev={ev} isAdmin={isAdmin} onEdit={onEdit} />
          ))}
        </div>
      </div>
    </GlassCard>
  );
}

function TimelineNode({ ev, isAdmin, onEdit }) {
  const meta = TIMELINE_STATUS[ev.status] || TIMELINE_STATUS.not_started;
  const Icon = meta.icon;
  return (
    <div className="flex flex-col items-center text-center gap-2 group" data-testid={`dp-timeline-node-${ev.kind}`}>
      <div className="text-3 text-[10px] uppercase tracking-wider min-h-[14px]">{formatDate(ev.date)}</div>
      <button
        type="button"
        onClick={() => isAdmin && onEdit(ev)}
        className={`relative h-9 w-9 rounded-full bg-[#0a1830] ring-2 ${
          ev.status === "completed" ? "ring-emerald-300/70 shadow-[0_0_18px_rgba(110,231,183,0.4)]"
          : ev.status === "in_progress" ? "ring-cyan-300/70 shadow-[0_0_18px_rgba(34,211,238,0.45)]"
          : ev.status === "at_risk" ? "ring-amber-300/70 shadow-[0_0_14px_rgba(251,191,36,0.4)]"
          : ev.status === "missed" ? "ring-rose-300/70 shadow-[0_0_14px_rgba(244,114,182,0.4)]"
          : "ring-white/15"
        } grid place-items-center ${isAdmin ? "cursor-pointer hover:scale-110 transition" : "cursor-default"}`}
        data-testid={`dp-timeline-dot-${ev.kind}`}
      >
        <Icon className={`h-4 w-4 ${meta.color}`} />
      </button>
      <div className="text-1 text-[11px] font-medium leading-tight px-1">{ev.label}</div>
      <StatusPill tone={meta.tone} className="text-[9px] py-0.5 px-1.5">{meta.label}</StatusPill>
      {ev.notes && (
        <div className="text-3 text-[10px] leading-snug line-clamp-2 px-1 group-hover:text-2 transition">{ev.notes}</div>
      )}
    </div>
  );
}

// ============================================================
// Risk Management
// ============================================================
function RiskBoard({ risks, isAdmin, onAdd, onEdit, onDelete }) {
  return (
    <GlassCard className="col-span-12 p-6" testId="dp-risk-board">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-1 font-semibold flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-rose-300" /> Risk Management
          </h3>
          <div className="text-3 text-xs mt-0.5">Risk titles, severity, team perception, and response strategies.</div>
        </div>
        {isAdmin && (
          <Button data-testid="dp-add-risk" onClick={onAdd} className="cta-primary">
            <Plus className="h-4 w-4" /> Add risk
          </Button>
        )}
      </div>

      {risks.length === 0 ? (
        <div className="text-2 text-sm py-6 text-center" data-testid="dp-risk-empty">No risks tracked yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {risks.map((r) => {
            const sev = SEVERITY_META[r.severity] || SEVERITY_META.medium;
            const status = RISK_STATUS[r.status] || RISK_STATUS.open;
            return (
              <div key={r.id} className="glass p-5 relative overflow-hidden" data-testid={`dp-risk-${r.id}`}>
                <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full pointer-events-none" style={{ background: `radial-gradient(closest-side, ${sev.glow}, transparent 70%)` }} />
                <div className="relative flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5">
                    <div className="h-9 w-9 rounded-lg bg-white/5 ring-1 ring-white/10 grid place-items-center text-rose-200">
                      <AlertTriangle className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-1 font-semibold leading-tight">{r.title}</div>
                      <div className="text-3 text-[11px] mt-0.5 capitalize">
                        {r.category ? RISK_CATEGORY_LABELS[r.category] || r.category.replace(/_/g, " ") : "Uncategorized"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <StatusPill tone={sev.tone}>Severity: {sev.label}</StatusPill>
                    {isAdmin && (
                      <>
                        <button onClick={() => onEdit(r)} className="text-2 hover:text-white p-1" data-testid={`dp-risk-edit-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => onDelete(r)} className="text-rose-300/70 hover:text-rose-300 p-1" data-testid={`dp-risk-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="relative mt-4 space-y-3">
                  <RiskField label="How teams may view it" value={r.team_perception} />
                  <RiskField label="Response strategy" value={r.response_strategy} accent />
                  <RiskField label="Development plan" value={r.development_plan} />
                  <RiskField label="Advisor recommendation" value={r.advisor_recommendation} accent />
                </div>

                <div className="relative mt-4 flex items-center justify-between gap-2 flex-wrap">
                  <StatusPill tone={status.tone}>{status.label}</StatusPill>
                  {r.owner_name && (
                    <div className="text-3 text-[11px]">Owner: <span className="text-2">{r.owner_name}</span></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}

function RiskField({ label, value, accent = false }) {
  return (
    <div className={accent ? "glass p-2.5" : ""}>
      <div className={`text-[10px] uppercase tracking-wider ${accent ? "text-cyan-200" : "text-3"}`}>{label}</div>
      <div className="text-2 text-[13px] leading-relaxed mt-1">
        {value ? value : <span className="text-3 italic">—</span>}
      </div>
    </div>
  );
}

// ============================================================
// Objectives Board (Phase 6B)
// ============================================================
function ObjectivesBoard({ objectives, currentUser, isAdmin, onAdd, onEdit, onDelete, onToggleStep, onMarkComplete }) {
  return (
    <GlassCard className="col-span-12 p-6" testId="dp-objectives-board">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-1 font-semibold flex items-center gap-2">
            <Flag className="h-4 w-4 text-cyan-300" /> Key Objectives
          </h3>
          <div className="text-3 text-xs mt-0.5">
            What needs to happen between now and draft week. Assignees can check off action steps.
          </div>
        </div>
        {isAdmin && (
          <Button data-testid="dp-add-objective" onClick={onAdd} className="cta-primary">
            <Plus className="h-4 w-4" /> Add objective
          </Button>
        )}
      </div>
      {objectives.length === 0 ? (
        <div className="text-2 text-sm py-6 text-center" data-testid="dp-objectives-empty">No objectives tracked yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {objectives.map((o) => (
            <ObjectiveCard
              key={o.id}
              objective={o}
              currentUser={currentUser}
              isAdmin={isAdmin}
              onEdit={() => onEdit(o)}
              onDelete={() => onDelete(o)}
              onToggleStep={(stepIdx, done) => onToggleStep(o, stepIdx, done)}
              onMarkComplete={() => onMarkComplete(o)}
            />
          ))}
        </div>
      )}
    </GlassCard>
  );
}

function ObjectiveCard({ objective, currentUser, isAdmin, onEdit, onDelete, onToggleStep, onMarkComplete }) {
  const o = objective;
  const priMeta = OBJECTIVE_PRIORITY_META[o.priority] || OBJECTIVE_PRIORITY_META.medium;
  const statusMeta = OBJECTIVE_STATUS_META[o.status] || OBJECTIVE_STATUS_META.not_started;
  const isOwner = currentUser?.id && currentUser.id === o.owner_user_id;
  const canEditSteps = isAdmin || isOwner;

  return (
    <div className="glass p-5 relative overflow-hidden" data-testid={`dp-objective-${o.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill tone={priMeta.tone}>{priMeta.label}</StatusPill>
            {o.category && (
              <span className="text-3 text-[11px] uppercase tracking-wider">
                {OBJECTIVE_CATEGORY_LABELS[o.category] || o.category.replace(/_/g, " ")}
              </span>
            )}
          </div>
          <div className="text-1 font-semibold leading-tight mt-2">{o.title}</div>
          <div className="flex items-center gap-3 mt-1.5 text-3 text-[11px] flex-wrap">
            <StatusPill tone={statusMeta.tone}>{statusMeta.label}</StatusPill>
            {o.owner_name && (
              <span className="flex items-center gap-1.5"><User className="h-3 w-3" /> {o.owner_name}{isOwner ? " (you)" : ""}</span>
            )}
            {o.due_date && (
              <span className="flex items-center gap-1.5"><CalendarIcon className="h-3 w-3" /> Due {formatDate(o.due_date)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isAdmin && (
            <>
              <button onClick={onEdit} className="text-2 hover:text-white p-1" data-testid={`dp-objective-edit-${o.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={onDelete} className="text-rose-300/70 hover:text-rose-300 p-1" data-testid={`dp-objective-delete-${o.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {o.advisor_note && (
        <div className="glass p-2.5 mt-3">
          <div className="text-[10px] uppercase tracking-wider text-cyan-200">Advisor note</div>
          <div className="text-2 text-[12px] leading-relaxed mt-1">{o.advisor_note}</div>
        </div>
      )}

      {(o.action_steps || []).length > 0 && (
        <div className="mt-3">
          <div className="text-3 text-[10px] uppercase tracking-wider mb-2">Action Steps</div>
          <ul className="space-y-1.5">
            {o.action_steps.map((s, idx) => (
              <li key={s.id || idx} className="flex items-start gap-2 group" data-testid={`dp-objective-${o.id}-step-${idx}`}>
                <Checkbox
                  checked={!!s.done}
                  disabled={!canEditSteps || o.status === "complete"}
                  onCheckedChange={(v) => onToggleStep(idx, !!v)}
                  className="mt-0.5 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#04111A]"
                />
                <span className={`text-2 text-[13px] leading-relaxed ${s.done ? "line-through text-3" : ""}`}>{s.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between text-3 text-[10px] uppercase tracking-wider">
          <span>Completion</span>
          <span className="text-1 font-mono">{o.completion_pct ?? 0}%</span>
        </div>
        <Progress value={o.completion_pct ?? 0} className="h-1.5 mt-1.5 bg-white/5" />
      </div>

      {canEditSteps && o.status !== "complete" && (
        <div className="mt-3 flex justify-end">
          <Button
            data-testid={`dp-objective-mark-${o.id}`}
            size="sm"
            variant="ghost"
            onClick={onMarkComplete}
            className="text-emerald-200 hover:text-emerald-100 hover:bg-emerald-300/10 h-7 px-2"
          >
            <Check className="h-3.5 w-3.5" /> Mark complete
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Weekly Priorities (Phase 6B)
// ============================================================
function WeeklyPrioritiesCard({ weekly, plan, currentUser, isAdmin, onEdit, onTogglePriority, onToggleTask }) {
  const wp = weekly || {};
  const priorities = wp.priorities || [];
  const tasks = wp.tasks || [];

  return (
    <GlassCard className="col-span-12 p-6 relative overflow-hidden" testId="dp-weekly-priorities">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.16), transparent 70%)" }} />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-1 font-semibold flex items-center gap-2">
              <ListTodo className="h-4 w-4 text-cyan-300" /> Weekly Draft Priorities
            </h3>
            <div className="text-3 text-xs mt-0.5">{wp.week_focus || <span className="italic">No focus set this week.</span>}</div>
          </div>
          <div className="flex items-center gap-2">
            {wp.next_meeting_date && (
              <StatusPill tone="ice">
                <CalendarIcon className="h-3 w-3" /> Next meeting {formatDate(wp.next_meeting_date)}
              </StatusPill>
            )}
            {isAdmin && (
              <Button data-testid="dp-edit-weekly" onClick={onEdit} variant="ghost" className="text-2 hover:text-white">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top 3 priorities */}
          <div className="glass p-4">
            <div className="text-3 text-[10px] uppercase tracking-wider mb-2">Top 3 Priorities</div>
            {priorities.length === 0 ? (
              <div className="text-2 text-sm">No priorities set.</div>
            ) : (
              <ol className="space-y-2">
                {priorities.slice(0, 3).map((p, idx) => {
                  const meta = WEEKLY_STATUS_META[p.status] || WEEKLY_STATUS_META.not_started;
                  const isOwner = currentUser?.id && currentUser.id === p.owner_user_id;
                  const canChange = isAdmin || isOwner;
                  return (
                    <li key={p.id} className="flex items-start gap-3" data-testid={`dp-weekly-priority-${p.id}`}>
                      <div className="h-6 w-6 rounded-full bg-cyan-300/15 ring-1 ring-cyan-300/30 grid place-items-center text-cyan-200 text-[11px] font-mono shrink-0 mt-0.5">{idx + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-1 text-sm font-medium ${p.status === "complete" ? "line-through text-3" : ""}`}>{p.title}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                          {p.owner_name && <span className="text-3 text-[11px]">{p.owner_name}{isOwner ? " (you)" : ""}</span>}
                          {canChange && p.status !== "complete" && (
                            <button
                              onClick={() => onTogglePriority(p, p.status === "in_progress" ? "complete" : "in_progress")}
                              className="text-cyan-200 hover:text-cyan-100 text-[11px]"
                              data-testid={`dp-weekly-priority-toggle-${p.id}`}
                            >
                              {p.status === "in_progress" ? "→ complete" : "→ in progress"}
                            </button>
                          )}
                          {canChange && p.status === "complete" && (
                            <button
                              onClick={() => onTogglePriority(p, "in_progress")}
                              className="text-2 hover:text-white text-[11px]"
                              data-testid={`dp-weekly-priority-reopen-${p.id}`}
                            >
                              reopen
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {/* Tasks */}
          <div className="glass p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-3 text-[10px] uppercase tracking-wider">Assigned Tasks</div>
              <div className="text-3 text-[11px] font-mono">
                {tasks.filter((t) => t.done).length}/{tasks.length} done
              </div>
            </div>
            {tasks.length === 0 ? (
              <div className="text-2 text-sm">No tasks assigned this week.</div>
            ) : (
              <ul className="space-y-1.5">
                {tasks.map((t) => {
                  const isOwner = currentUser?.id && currentUser.id === t.owner_user_id;
                  const canCheck = isAdmin || isOwner;
                  return (
                    <li key={t.id} className="flex items-start gap-2" data-testid={`dp-weekly-task-${t.id}`}>
                      <Checkbox
                        checked={!!t.done}
                        disabled={!canCheck}
                        onCheckedChange={(v) => onToggleTask(t, !!v)}
                        className="mt-0.5 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#04111A]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`text-2 text-[13px] leading-relaxed ${t.done ? "line-through text-3" : ""}`}>{t.text}</div>
                        {t.owner_name && <div className="text-3 text-[11px] mt-0.5">{t.owner_name}{isOwner ? " (you)" : ""}</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {wp.advisor_notes && (
          <div className="glass p-3 mt-4">
            <div className="text-[10px] uppercase tracking-wider text-cyan-200 flex items-center gap-1.5"><Quote className="h-3 w-3" /> Advisor Notes</div>
            <div className="text-2 text-sm leading-relaxed mt-1">{wp.advisor_notes}</div>
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ============================================================
// Editors (Phase 6B): Objective + Weekly Priorities
// ============================================================
const EMPTY_OBJECTIVE = {
  title: "", category: "", priority: "medium", status: "not_started",
  due_date: "", advisor_note: "", action_steps: [],
  completion_pct: 0, owner_user_id: "",
};

function ObjectiveEditor({ open, onOpenChange, objective, assignableUsers, onSave }) {
  const [form, setForm] = useState(EMPTY_OBJECTIVE);
  const [stepDraft, setStepDraft] = useState("");

  useEffect(() => {
    if (open) {
      if (objective) {
        setForm({
          title: objective.title || "",
          category: objective.category || "",
          priority: objective.priority || "medium",
          status: objective.status || "not_started",
          due_date: objective.due_date ? objective.due_date.slice(0, 10) : "",
          advisor_note: objective.advisor_note || "",
          action_steps: objective.action_steps || [],
          completion_pct: objective.completion_pct ?? 0,
          owner_user_id: objective.owner_user_id || "",
        });
      } else {
        setForm(EMPTY_OBJECTIVE);
      }
      setStepDraft("");
    }
  }, [open, objective]);

  const addStep = () => {
    if (!stepDraft.trim()) return;
    setForm((f) => ({
      ...f,
      action_steps: [...f.action_steps, { id: `tmp-${Date.now()}`, text: stepDraft.trim(), done: false }],
    }));
    setStepDraft("");
  };
  const removeStep = (i) => setForm((f) => ({ ...f, action_steps: f.action_steps.filter((_, idx) => idx !== i) }));

  const submit = () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    const payload = {
      ...form,
      due_date: form.due_date || null,
      category: form.category || null,
      owner_user_id: form.owner_user_id || null,
      completion_pct: Number(form.completion_pct) || 0,
    };
    onSave(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-2xl text-1" data-testid="dp-objective-editor">
        <DialogHeader><DialogTitle>{objective ? "Edit" : "Add"} Objective</DialogTitle></DialogHeader>
        <div className="grid grid-cols-12 gap-3 max-h-[65vh] overflow-y-auto pr-1">
          <Field label="Title *" colSpan={12} testId="obj-title">
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Category" colSpan={6} testId="obj-category">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 max-h-72">
                {Object.entries(OBJECTIVE_CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority" colSpan={3} testId="obj-priority">
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status" colSpan={3} testId="obj-status">
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Due Date" colSpan={4} testId="obj-due">
            <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Owner" colSpan={5} testId="obj-owner">
            <Select value={form.owner_user_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, owner_user_id: v === "__none__" ? "" : v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 max-h-72">
                <SelectItem value="__none__">Unassigned</SelectItem>
                {assignableUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name} <span className="text-3 text-xs">· {u.role}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Completion %" colSpan={3} testId="obj-pct">
            <Input type="number" min="0" max="100" value={form.completion_pct} onChange={(e) => setForm((f) => ({ ...f, completion_pct: e.target.value }))} className="bg-white/5 border-white/10 text-1 font-mono" />
          </Field>
          <Field label="Advisor note" colSpan={12} testId="obj-note">
            <Textarea rows={2} value={form.advisor_note} onChange={(e) => setForm((f) => ({ ...f, advisor_note: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <div className="col-span-12">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Action Steps</Label>
            <div className="space-y-1.5 mt-1.5">
              {form.action_steps.map((s, i) => (
                <div key={s.id || i} className="flex items-center gap-2 glass p-2">
                  <Checkbox checked={!!s.done} onCheckedChange={(v) => setForm((f) => ({ ...f, action_steps: f.action_steps.map((x, idx) => idx === i ? { ...x, done: !!v } : x) }))} className="border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#04111A]" />
                  <Input value={s.text} onChange={(e) => setForm((f) => ({ ...f, action_steps: f.action_steps.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x) }))} className="bg-transparent border-0 text-1 h-7 px-1" />
                  <button onClick={() => removeStep(i)} className="text-rose-300/70 hover:text-rose-300 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={stepDraft} onChange={(e) => setStepDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStep()} placeholder="Add a step…" className="bg-white/5 border-white/10 text-1" data-testid="obj-step-input" />
                <Button data-testid="obj-step-add" onClick={addStep} variant="ghost" className="text-2 hover:text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="dp-objective-save" onClick={submit} className="cta-primary">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WeeklyPrioritiesEditor({ open, onOpenChange, weekly, assignableUsers, onSave }) {
  const initial = () => ({
    week_focus: weekly?.week_focus || "",
    priorities: weekly?.priorities?.length ? [...weekly.priorities] : [{ id: `tmp-${Date.now()}-1`, title: "", status: "not_started", owner_user_id: null }, { id: `tmp-${Date.now()}-2`, title: "", status: "not_started", owner_user_id: null }, { id: `tmp-${Date.now()}-3`, title: "", status: "not_started", owner_user_id: null }],
    tasks: weekly?.tasks ? [...weekly.tasks] : [],
    advisor_notes: weekly?.advisor_notes || "",
    next_meeting_date: weekly?.next_meeting_date ? weekly.next_meeting_date.slice(0, 10) : "",
  });
  const [form, setForm] = useState(initial);
  const [taskDraft, setTaskDraft] = useState("");
  const [taskOwner, setTaskOwner] = useState("__none__");
  useEffect(() => { if (open) { setForm(initial()); setTaskDraft(""); setTaskOwner("__none__"); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, weekly]);

  const updatePriority = (i, patch) => setForm((f) => ({ ...f, priorities: f.priorities.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  const addTask = () => {
    if (!taskDraft.trim()) return;
    setForm((f) => ({ ...f, tasks: [...f.tasks, { id: `tmp-${Date.now()}`, text: taskDraft.trim(), done: false, owner_user_id: taskOwner === "__none__" ? null : taskOwner }] }));
    setTaskDraft("");
    setTaskOwner("__none__");
  };
  const removeTask = (i) => setForm((f) => ({ ...f, tasks: f.tasks.filter((_, idx) => idx !== i) }));

  const submit = () => {
    const payload = {
      week_focus: form.week_focus,
      priorities: form.priorities.filter((p) => (p.title || "").trim()),
      tasks: form.tasks,
      advisor_notes: form.advisor_notes,
      next_meeting_date: form.next_meeting_date || null,
    };
    onSave(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-3xl text-1" data-testid="dp-weekly-editor">
        <DialogHeader><DialogTitle>Edit Weekly Priorities</DialogTitle></DialogHeader>
        <div className="grid grid-cols-12 gap-3 max-h-[65vh] overflow-y-auto pr-1">
          <Field label="This week's focus" colSpan={9} testId="wp-focus">
            <Input value={form.week_focus} onChange={(e) => setForm((f) => ({ ...f, week_focus: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Next meeting" colSpan={3} testId="wp-meeting">
            <Input type="date" value={form.next_meeting_date} onChange={(e) => setForm((f) => ({ ...f, next_meeting_date: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <div className="col-span-12">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Top 3 Priorities</Label>
            <div className="space-y-2 mt-1.5">
              {form.priorities.slice(0, 3).map((p, i) => (
                <div key={p.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`wp-priority-row-${i}`}>
                  <div className="col-span-1 text-3 font-mono text-sm pl-2">{i + 1}.</div>
                  <Input value={p.title} onChange={(e) => updatePriority(i, { title: e.target.value })} placeholder="Priority…" className="col-span-6 bg-white/5 border-white/10 text-1" />
                  <Select value={p.owner_user_id || "__none__"} onValueChange={(v) => updatePriority(i, { owner_user_id: v === "__none__" ? null : v })}>
                    <SelectTrigger className="col-span-3 bg-white/5 border-white/10 text-1"><SelectValue placeholder="Owner" /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10">
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {assignableUsers.map((u) => (<SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={p.status} onValueChange={(v) => updatePriority(i, { status: v })}>
                    <SelectTrigger className="col-span-2 bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10">
                      <SelectItem value="not_started">Not started</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Tasks</Label>
            <div className="space-y-1.5 mt-1.5">
              {form.tasks.map((t, i) => (
                <div key={t.id || i} className="flex items-center gap-2 glass p-2">
                  <Checkbox checked={!!t.done} onCheckedChange={(v) => setForm((f) => ({ ...f, tasks: f.tasks.map((x, idx) => idx === i ? { ...x, done: !!v } : x) }))} className="border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#04111A]" />
                  <Input value={t.text} onChange={(e) => setForm((f) => ({ ...f, tasks: f.tasks.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x) }))} className="bg-transparent border-0 text-1 h-7 px-1 flex-1" />
                  <Select value={t.owner_user_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, tasks: f.tasks.map((x, idx) => idx === i ? { ...x, owner_user_id: v === "__none__" ? null : v } : x) }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-1 h-8 w-40 text-xs"><SelectValue placeholder="Owner" /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10">
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {assignableUsers.map((u) => (<SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <button onClick={() => removeTask(i)} className="text-rose-300/70 hover:text-rose-300 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={taskDraft} onChange={(e) => setTaskDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} placeholder="Add a task…" className="bg-white/5 border-white/10 text-1" data-testid="wp-task-input" />
                <Select value={taskOwner} onValueChange={setTaskOwner}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-1 w-44"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent className="glass-strong border-white/10">
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {assignableUsers.map((u) => (<SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Button data-testid="wp-task-add" onClick={addTask} variant="ghost" className="text-2 hover:text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
          <Field label="Advisor notes" colSpan={12} testId="wp-notes">
            <Textarea rows={3} value={form.advisor_notes} onChange={(e) => setForm((f) => ({ ...f, advisor_notes: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="dp-weekly-save" onClick={submit} className="cta-primary">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Editors (dialogs)
// ============================================================
function MarkdownEditor({ open, onOpenChange, title, value, onSave }) {
  const [text, setText] = useState(value || "");
  useEffect(() => { if (open) setText(value || ""); }, [open, value]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-2xl text-1" data-testid="dp-md-editor">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} className="bg-white/5 border-white/10 text-1" placeholder="Markdown supported…" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="dp-md-editor-save" className="cta-primary" onClick={() => { onSave(text); onOpenChange(false); }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScenarioEditor({ open, onOpenChange, scenarioKey, scenario, onSave }) {
  const [form, setForm] = useState({ projected_range: "", required: "", concerns: "", response_plan: "", advisor_note: "" });
  useEffect(() => {
    if (open) {
      setForm({
        projected_range: scenario?.projected_range || "",
        required: scenario?.required || "",
        concerns: scenario?.concerns || "",
        response_plan: scenario?.response_plan || "",
        advisor_note: scenario?.advisor_note || "",
      });
    }
  }, [open, scenario]);
  const meta = SCENARIO_META[scenarioKey] || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-2xl text-1" data-testid={`dp-scenario-editor-${scenarioKey}`}>
        <DialogHeader><DialogTitle>Edit {meta.label} Scenario</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <Field label="Projected Draft Range" testId="scn-range">
            <Input value={form.projected_range} onChange={(e) => setForm((f) => ({ ...f, projected_range: e.target.value }))} className="bg-white/5 border-white/10 text-1" placeholder="e.g., Round 3" />
          </Field>
          <Field label="What must happen" testId="scn-required">
            <Textarea rows={2} value={form.required} onChange={(e) => setForm((f) => ({ ...f, required: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="What could hurt this" testId="scn-concerns">
            <Textarea rows={2} value={form.concerns} onChange={(e) => setForm((f) => ({ ...f, concerns: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Response plan" testId="scn-response">
            <Textarea rows={2} value={form.response_plan} onChange={(e) => setForm((f) => ({ ...f, response_plan: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Advisor note" testId="scn-note">
            <Textarea rows={2} value={form.advisor_note} onChange={(e) => setForm((f) => ({ ...f, advisor_note: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="dp-scenario-save" className="cta-primary" onClick={() => { onSave({ key: scenarioKey, ...form }); onOpenChange(false); }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TimelineEditor({ open, onOpenChange, event, onSave }) {
  const [form, setForm] = useState({ date: "", status: "not_started", notes: "" });
  useEffect(() => {
    if (open && event) {
      setForm({
        date: event.date ? event.date.slice(0, 10) : "",
        status: event.status || "not_started",
        notes: event.notes || "",
      });
    }
  }, [open, event]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-lg text-1" data-testid="dp-timeline-editor">
        <DialogHeader><DialogTitle>Edit · {event?.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Date" testId="tl-date">
            <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Status" testId="tl-status">
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {Object.entries(TIMELINE_STATUS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Notes" testId="tl-notes">
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="dp-timeline-save" className="cta-primary" onClick={() => { onSave({ ...form, date: form.date || null }); onOpenChange(false); }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_RISK = {
  title: "", category: "", severity: "medium", team_perception: "",
  response_strategy: "", development_plan: "", advisor_recommendation: "",
  status: "open",
};

function RiskEditor({ open, onOpenChange, risk, onSave }) {
  const [form, setForm] = useState(EMPTY_RISK);
  useEffect(() => {
    if (open) {
      if (risk) {
        setForm({
          title: risk.title || "",
          category: risk.category || "",
          severity: risk.severity || "medium",
          team_perception: risk.team_perception || "",
          response_strategy: risk.response_strategy || "",
          development_plan: risk.development_plan || "",
          advisor_recommendation: risk.advisor_recommendation || "",
          status: risk.status || "open",
        });
      } else {
        setForm(EMPTY_RISK);
      }
    }
  }, [open, risk]);
  const submit = () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    onSave({ ...form, category: form.category || null });
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-2xl text-1" data-testid="dp-risk-editor">
        <DialogHeader><DialogTitle>{risk ? "Edit" : "Add"} Risk</DialogTitle></DialogHeader>
        <div className="grid grid-cols-12 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <Field label="Title *" colSpan={12} testId="risk-title">
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Category" colSpan={6} testId="risk-category">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 max-h-72">
                {Object.entries(RISK_CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Severity" colSpan={3} testId="risk-severity">
            <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status" colSpan={3} testId="risk-status">
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="monitoring">Monitoring</SelectItem>
                <SelectItem value="mitigated">Mitigated</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="How teams may view it" colSpan={12} testId="risk-perception">
            <Textarea rows={2} value={form.team_perception} onChange={(e) => setForm((f) => ({ ...f, team_perception: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Response strategy" colSpan={12} testId="risk-response">
            <Textarea rows={2} value={form.response_strategy} onChange={(e) => setForm((f) => ({ ...f, response_strategy: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Development plan" colSpan={12} testId="risk-development">
            <Textarea rows={2} value={form.development_plan} onChange={(e) => setForm((f) => ({ ...f, development_plan: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
          <Field label="Advisor recommendation" colSpan={12} testId="risk-recommendation">
            <Textarea rows={2} value={form.advisor_recommendation} onChange={(e) => setForm((f) => ({ ...f, advisor_recommendation: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="dp-risk-save" className="cta-primary" onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, colSpan = 12, testId }) {
  const span = `col-span-${colSpan}`;
  return (
    <div className={span} data-testid={testId}>
      <Label className="text-3 text-[10px] uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ============================================================
// AI Strategy Review Card (Phase 6C)
// ============================================================
function AIStrategyReviewCard({ draft, sectionsToApply, setSectionsToApply, onApply, onReject }) {
  if (!draft) return null;
  const scenarioLabels = { best_case: "Best Case", expected: "Expected", risk: "Risk" };
  const sectionItems = [
    { key: "positioning", label: "Positioning summary", count: draft.positioning_summary_md ? 1 : 0 },
    { key: "explanation", label: "Stock explanation", count: draft.ai_explanation_md ? 1 : 0 },
    { key: "scenarios", label: "Draft scenarios", count: (draft.scenarios || []).length },
    { key: "weekly", label: "Weekly priorities", count: (draft.weekly_priorities?.priorities?.length || 0) + (draft.weekly_priorities?.tasks?.length || 0) },
    { key: "risks", label: "Risks (appended)", count: (draft.risk_analysis || []).length },
  ];
  return (
    <GlassCard className="col-span-12 p-6 relative overflow-hidden" testId="dp-ai-review-card">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(167,139,250,0.25), transparent 70%)" }} />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-1 font-semibold flex items-center gap-2"><Wand2 className="h-4 w-4 text-violet-300" /> AI Strategy Draft</h3>
            <div className="text-3 text-xs mt-0.5">
              Generated {new Date(draft.generated_at).toLocaleString()} · Review & approve before applying to plan.
            </div>
          </div>
          <StatusPill tone={draft.status === "approved" ? "emerald" : "amber"}>
            {draft.status === "approved" ? "Applied" : "Awaiting approval"}
          </StatusPill>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {draft.positioning_summary_md && (
            <AISection label="Positioning Summary" tone="cyan">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.positioning_summary_md}</ReactMarkdown>
            </AISection>
          )}
          {draft.ai_explanation_md && (
            <AISection label="Stock Explanation" tone="cyan">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.ai_explanation_md}</ReactMarkdown>
            </AISection>
          )}
          {(draft.scenarios || []).length > 0 && (
            <AISection label="Scenarios" tone="violet" className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {draft.scenarios.map((s) => (
                  <div key={s.key} className="glass p-3">
                    <div className="text-[10px] uppercase tracking-wider text-violet-200">{scenarioLabels[s.key] || s.key}</div>
                    <div className="text-1 text-sm font-semibold mt-0.5">{s.projected_range || "—"}</div>
                    <div className="text-2 text-[12px] mt-1"><strong>Required:</strong> {s.required || "—"}</div>
                    <div className="text-2 text-[12px] mt-1"><strong>Concerns:</strong> {s.concerns || "—"}</div>
                    <div className="text-2 text-[12px] mt-1"><strong>Response:</strong> {s.response_plan || "—"}</div>
                  </div>
                ))}
              </div>
            </AISection>
          )}
          {draft.weekly_priorities && (draft.weekly_priorities.priorities?.length || draft.weekly_priorities.tasks?.length) && (
            <AISection label="Weekly Priorities" tone="cyan">
              {draft.weekly_priorities.week_focus && <div className="text-2 text-[13px] mb-2"><strong>Focus:</strong> {draft.weekly_priorities.week_focus}</div>}
              {draft.weekly_priorities.priorities?.length > 0 && (
                <ol className="list-decimal list-inside space-y-1">
                  {draft.weekly_priorities.priorities.slice(0, 3).map((p, i) => (
                    <li key={i} className="text-2 text-[13px]">{p.title}{p.owner_hint ? ` (${p.owner_hint})` : ""}</li>
                  ))}
                </ol>
              )}
              {draft.weekly_priorities.tasks?.length > 0 && (
                <div className="mt-2 text-3 text-[11px]">+ {draft.weekly_priorities.tasks.length} tasks</div>
              )}
            </AISection>
          )}
          {(draft.risk_analysis || []).length > 0 && (
            <AISection label="Suggested Risks" tone="rose">
              <ul className="space-y-1.5">
                {draft.risk_analysis.map((r, i) => (
                  <li key={i} className="text-2 text-[13px]">
                    <span className="text-1 font-medium">{r.title}</span>
                    <span className="text-3 text-[11px] ml-2">[{r.severity}]</span>
                  </li>
                ))}
              </ul>
            </AISection>
          )}
          {(draft.talking_points || []).length > 0 && (
            <AISection label="Talking Points" tone="ice">
              <ul className="list-disc list-inside space-y-1">
                {draft.talking_points.map((t, i) => <li key={i} className="text-2 text-[13px]">{t}</li>)}
              </ul>
            </AISection>
          )}
          {draft.family_friendly_summary_md && (
            <AISection label="Family-Friendly Summary" tone="emerald" className="lg:col-span-2">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.family_friendly_summary_md}</ReactMarkdown>
            </AISection>
          )}
        </div>

        {/* Apply controls */}
        <div className="mt-5 glass p-4">
          <div className="text-3 text-[10px] uppercase tracking-wider mb-2">Apply to plan</div>
          <div className="flex items-center gap-x-6 gap-y-2 flex-wrap">
            {sectionItems.filter((s) => s.count > 0).map((s) => (
              <label key={s.key} className="flex items-center gap-2 text-2 text-sm cursor-pointer" data-testid={`dp-ai-apply-${s.key}`}>
                <Checkbox
                  checked={!!sectionsToApply[s.key]}
                  onCheckedChange={(v) => setSectionsToApply((p) => ({ ...p, [s.key]: !!v }))}
                  className="border-white/20 data-[state=checked]:bg-violet-400 data-[state=checked]:text-[#04111A]"
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button data-testid="dp-ai-reject" onClick={onReject} variant="ghost" className="text-rose-200 hover:text-rose-100 hover:bg-rose-300/10">
              <X className="h-4 w-4" /> Discard draft
            </Button>
            <Button data-testid="dp-ai-apply" onClick={onApply} className="cta-primary">
              <Check className="h-4 w-4" /> Apply selected to plan
            </Button>
          </div>
          <div className="text-3 text-[10px] mt-2 italic">
            Applied sections overwrite matching fields (positioning, explanation, scenarios, weekly priorities). "Risks" are appended and will not replace existing entries.
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

function AISection({ label, tone, children, className = "" }) {
  const ringByTone = {
    cyan: "ring-cyan-300/25", violet: "ring-violet-300/25", rose: "ring-rose-300/25",
    emerald: "ring-emerald-300/25", ice: "ring-white/15",
  };
  const textByTone = {
    cyan: "text-cyan-200", violet: "text-violet-200", rose: "text-rose-200",
    emerald: "text-emerald-200", ice: "text-white/70",
  };
  return (
    <div className={`glass p-3 ring-1 ${ringByTone[tone] || "ring-white/10"} ${className}`}>
      <div className={`text-[10px] uppercase tracking-wider ${textByTone[tone] || "text-3"}`}>{label}</div>
      <div className="markdown-body text-2 text-[12.5px] leading-relaxed mt-1">{children}</div>
    </div>
  );
}

// ============================================================
// Gated Placeholder (non-admin, plan unpublished)
// ============================================================
function GatedPlaceholder({ player }) {
  return (
    <GlassCard className="p-10 text-center relative overflow-hidden" testId="dp-gated-placeholder">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.12), transparent 70%)" }} />
      <div className="relative max-w-lg mx-auto">
        <div className="h-14 w-14 rounded-full bg-white/5 ring-1 ring-white/10 grid place-items-center mx-auto text-3">
          <Lock className="h-6 w-6" />
        </div>
        <h2 className="text-1 text-xl font-semibold mt-4">Draft plan in preparation</h2>
        <p className="text-2 text-sm mt-2 leading-relaxed">
          Your advisor is finalizing {player?.full_name ? `${player.full_name}'s` : "the"} draft strategy.
          Once published, you'll see the full roadmap — positioning, timeline, objectives, and risk management — here.
        </p>
        <p className="text-3 text-[11px] mt-4 italic">
          Check back soon, or reach out to your advisor via Messaging if you have questions.
        </p>
      </div>
    </GlassCard>
  );
}

// ============================================================
// Page
// ============================================================
export default function DraftPlanPage() {
  const { user } = useAuth();
  const playerId = useResolvedPlayerId();
  const isAdmin = user?.role === "admin";
  const [player, setPlayer] = useState(null);
  const [plan, setPlan] = useState(null);
  const [events, setEvents] = useState([]);
  const [risks, setRisks] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mdEditor, setMdEditor] = useState(null);
  const [scenarioEditor, setScenarioEditor] = useState(null);
  const [timelineEditor, setTimelineEditor] = useState(null);
  const [riskEditor, setRiskEditor] = useState({ open: false, risk: null });
  const [objectiveEditor, setObjectiveEditor] = useState({ open: false, obj: null });
  const [weeklyEditorOpen, setWeeklyEditorOpen] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [applyingSections, setApplyingSections] = useState({
    positioning: true, explanation: true, scenarios: true, weekly: true, risks: true,
  });

  const load = async () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [p, pl] = await Promise.all([
        api.get(`/players/${playerId}`),
        api.get("/draft-plans", { params: { player_id: playerId } }),
      ]);
      setPlayer(p.data);
      setPlan(pl.data);
      // If non-admin and plan is gated (unpublished), skip derived collection fetches
      // since the publish_gate will return 403 for them.
      const gated = pl.data && pl.data.gated === true;
      if (gated) {
        setEvents([]);
        setRisks([]);
        setObjectives([]);
        setAssignableUsers([]);
        return;
      }
      const [ev, rs, obs] = await Promise.all([
        api.get("/timeline-events", { params: { player_id: playerId } }),
        api.get("/risks", { params: { player_id: playerId } }),
        api.get("/objectives", { params: { player_id: playerId } }),
      ]);
      setEvents(ev.data || []);
      setRisks(rs.data || []);
      setObjectives(obs.data || []);
      try {
        const u = await api.get("/objectives/config/assignable-users", { params: { player_id: playerId } });
        setAssignableUsers(u.data || []);
      } catch { setAssignableUsers([]); }
    } catch (e) {
      toast.error("Failed to load draft plan");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [playerId]);

  const scenarioByKey = useMemo(() => {
    const out = {};
    (plan?.scenarios || []).forEach((s) => { out[s.key] = s; });
    return out;
  }, [plan]);

  // ---- mutations
  const saveMarkdown = async (field, text) => {
    if (!plan) return;
    try {
      const r = await api.patch(`/draft-plans/${plan.id}`, { [field]: text });
      setPlan(r.data);
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const saveScenario = async (s) => {
    if (!plan) return;
    const merged = { ...scenarioByKey, [s.key]: s };
    const ordered = ["best_case", "expected", "risk"].map((k) => merged[k] || { key: k });
    try {
      const r = await api.patch(`/draft-plans/${plan.id}`, { scenarios: ordered });
      setPlan(r.data);
      toast.success(`${SCENARIO_META[s.key].label} updated`);
    } catch (e) { toast.error("Save failed"); }
  };

  const saveTimeline = async (ev, patch) => {
    try {
      const r = await api.patch(`/timeline-events/${ev.id}`, patch);
      setEvents((arr) => arr.map((e) => e.id === r.data.id ? r.data : e));
      toast.success(`${ev.label} updated`);
    } catch (e) { toast.error("Save failed"); }
  };

  const saveRisk = async (form) => {
    try {
      if (riskEditor.risk) {
        const r = await api.patch(`/risks/${riskEditor.risk.id}`, form);
        setRisks((arr) => arr.map((x) => x.id === r.data.id ? r.data : x));
        toast.success("Risk updated");
      } else {
        const r = await api.post("/risks", { player_id: playerId, ...form });
        setRisks((arr) => [...arr, r.data]);
        toast.success("Risk added");
      }
    } catch (e) { toast.error("Save failed"); }
  };

  const deleteRisk = async (r) => {
    if (!window.confirm(`Delete risk "${r.title}"?`)) return;
    try {
      await api.delete(`/risks/${r.id}`);
      setRisks((arr) => arr.filter((x) => x.id !== r.id));
      toast.success("Risk removed");
    } catch (e) { toast.error("Delete failed"); }
  };

  const togglePublish = async () => {
    if (!plan) return;
    try {
      const r = await api.patch(`/draft-plans/${plan.id}`, { published: !plan.published });
      setPlan(r.data);
      toast.success(r.data.published ? "Plan published" : "Plan unpublished");
    } catch (e) { toast.error("Failed"); }
  };

  const generateAI = async () => {
    if (!plan) return;
    setGeneratingAI(true);
    try {
      const r = await api.post(`/draft-plans/${plan.id}/ai-generate`);
      setPlan(r.data);
      toast.success("AI strategy draft ready for your review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally {
      setGeneratingAI(false);
    }
  };

  const applyAI = async () => {
    if (!plan) return;
    const sections = Object.entries(applyingSections).filter(([_, v]) => v).map(([k]) => k);
    if (!sections.length) { toast.error("Select at least one section to apply"); return; }
    try {
      const r = await api.post(`/draft-plans/${plan.id}/ai-apply`, { sections });
      setPlan(r.data);
      // Reload dependent collections in case risks were inserted
      const [rs, obs] = await Promise.all([
        api.get("/risks", { params: { player_id: playerId } }),
        api.get("/objectives", { params: { player_id: playerId } }),
      ]);
      setRisks(rs.data || []);
      setObjectives(obs.data || []);
      toast.success("AI suggestions applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    }
  };

  const rejectAI = async () => {
    if (!plan) return;
    if (!window.confirm("Discard the AI strategy draft?")) return;
    try {
      await api.post(`/draft-plans/${plan.id}/ai-reject`);
      setPlan((p) => p ? { ...p, ai_strategy_draft: null } : p);
      toast.success("AI draft discarded");
    } catch (e) { toast.error("Failed"); }
  };

  const exportPdf = async () => {
    if (!plan) return;
    setExporting(true);
    try {
      const token = getStoredToken();
      const res = await fetch(`${API}/draft-plans/${plan.id}/export.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OmniDraftLab_${(player?.full_name || "Player").replace(/\s+/g, "_")}_Draft_Plan.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  // ---- Phase 6B handlers
  const saveObjective = async (form) => {
    try {
      if (objectiveEditor.obj) {
        const r = await api.patch(`/objectives/${objectiveEditor.obj.id}`, form);
        setObjectives((arr) => arr.map((x) => x.id === r.data.id ? r.data : x));
        toast.success("Objective updated");
      } else {
        const r = await api.post("/objectives", { player_id: playerId, ...form });
        setObjectives((arr) => [...arr, r.data]);
        toast.success("Objective added");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteObjective = async (o) => {
    if (!window.confirm(`Delete objective "${o.title}"?`)) return;
    try {
      await api.delete(`/objectives/${o.id}`);
      setObjectives((arr) => arr.filter((x) => x.id !== o.id));
      toast.success("Objective removed");
    } catch (e) { toast.error("Delete failed"); }
  };
  const toggleStep = async (o, idx, done) => {
    const newSteps = (o.action_steps || []).map((s, i) => i === idx ? { ...s, done } : s);
    const totalSteps = newSteps.length || 0;
    const doneSteps = newSteps.filter((s) => s.done).length;
    const newPct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : (o.completion_pct ?? 0);
    try {
      const r = await api.patch(`/objectives/${o.id}`, { action_steps: newSteps, completion_pct: newPct });
      setObjectives((arr) => arr.map((x) => x.id === r.data.id ? r.data : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const markObjectiveComplete = async (o) => {
    try {
      const r = await api.patch(`/objectives/${o.id}`, { status: "complete", completion_pct: 100 });
      setObjectives((arr) => arr.map((x) => x.id === r.data.id ? r.data : x));
      toast.success(`${o.title} marked complete`);
    } catch (e) { toast.error("Save failed"); }
  };

  const saveWeekly = async (form) => {
    if (!plan) return;
    try {
      const r = await api.patch(`/draft-plans/${plan.id}`, { weekly_priorities: form });
      setPlan(r.data);
      toast.success("Weekly priorities saved");
    } catch (e) { toast.error("Save failed"); }
  };
  const togglePriorityStatus = async (p, status) => {
    if (!plan) return;
    try {
      const r = await api.patch(`/draft-plans/${plan.id}/weekly-priorities/${p.id}`, { status });
      setPlan(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggleTask = async (t, done) => {
    if (!plan) return;
    try {
      const r = await api.patch(`/draft-plans/${plan.id}/weekly-tasks/${t.id}`, { done });
      setPlan(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  if (loading) return <LoadingState />;
  if (!playerId) return null;

  return (
    <div className="max-w-[1500px] mx-auto">
      <PlayerSubNav />
      <PageHeader
        title="Draft Plan"
        subtitle={player ? `Strategic roadmap for ${player.full_name} from now through draft weekend.` : "Strategic roadmap from now through draft weekend."}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {plan?.published ? <StatusPill tone="emerald">Published</StatusPill> : <StatusPill tone="neutral">Draft</StatusPill>}
            {isAdmin && (
              <>
                <Button data-testid="dp-generate-ai" onClick={generateAI} disabled={generatingAI} variant="ghost" className="text-2 hover:text-white">
                  <Wand2 className="h-4 w-4" /> {generatingAI ? "Generating…" : "Generate AI Strategy"}
                </Button>
                <Button data-testid="dp-export-pdf" onClick={exportPdf} disabled={exporting} variant="ghost" className="text-2 hover:text-white">
                  <FileDown className="h-4 w-4" /> {exporting ? "Exporting…" : "Export PDF"}
                </Button>
                <Button data-testid="dp-toggle-publish" onClick={togglePublish} variant="ghost" className="text-2 hover:text-white">
                  {plan?.published ? "Unpublish" : "Publish"}
                </Button>
              </>
            )}
          </div>
        }
      />

      {!isAdmin && plan && (plan.gated === true || !plan.published) ? (
        <GatedPlaceholder player={player} />
      ) : (
      <div className="grid grid-cols-12 gap-5">
        <PositioningHero
          player={player}
          plan={plan}
          scenarioByKey={scenarioByKey}
          isAdmin={isAdmin}
          onEditSummary={(field) => setMdEditor(field)}
        />
        {isAdmin && plan?.ai_strategy_draft && (
          <AIStrategyReviewCard
            draft={plan.ai_strategy_draft}
            sectionsToApply={applyingSections}
            setSectionsToApply={setApplyingSections}
            onApply={applyAI}
            onReject={rejectAI}
          />
        )}
        <ScenarioBoard
          scenarioByKey={scenarioByKey}
          isAdmin={isAdmin}
          onEdit={(key, scenario) => setScenarioEditor({ key, scenario })}
        />
        <Timeline
          events={events}
          isAdmin={isAdmin}
          onEdit={(ev) => setTimelineEditor(ev)}
        />
        <WeeklyPrioritiesCard
          weekly={plan?.weekly_priorities}
          plan={plan}
          currentUser={user}
          isAdmin={isAdmin}
          onEdit={() => setWeeklyEditorOpen(true)}
          onTogglePriority={togglePriorityStatus}
          onToggleTask={toggleTask}
        />
        <ObjectivesBoard
          objectives={objectives}
          currentUser={user}
          isAdmin={isAdmin}
          onAdd={() => setObjectiveEditor({ open: true, obj: null })}
          onEdit={(o) => setObjectiveEditor({ open: true, obj: o })}
          onDelete={deleteObjective}
          onToggleStep={toggleStep}
          onMarkComplete={markObjectiveComplete}
        />
        <RiskBoard
          risks={risks}
          isAdmin={isAdmin}
          onAdd={() => setRiskEditor({ open: true, risk: null })}
          onEdit={(r) => setRiskEditor({ open: true, risk: r })}
          onDelete={deleteRisk}
        />
      </div>
      )}

      {/* Editors */}
      <MarkdownEditor
        open={!!mdEditor}
        onOpenChange={(o) => { if (!o) setMdEditor(null); }}
        title={mdEditor === "ai_explanation_md" ? "Edit AI Explanation" : "Edit Advisor Summary"}
        value={mdEditor ? plan?.[mdEditor] : ""}
        onSave={(text) => mdEditor && saveMarkdown(mdEditor, text)}
      />
      <ScenarioEditor
        open={!!scenarioEditor}
        onOpenChange={(o) => { if (!o) setScenarioEditor(null); }}
        scenarioKey={scenarioEditor?.key}
        scenario={scenarioEditor?.scenario}
        onSave={saveScenario}
      />
      <TimelineEditor
        open={!!timelineEditor}
        onOpenChange={(o) => { if (!o) setTimelineEditor(null); }}
        event={timelineEditor}
        onSave={(patch) => timelineEditor && saveTimeline(timelineEditor, patch)}
      />
      <RiskEditor
        open={riskEditor.open}
        onOpenChange={(o) => setRiskEditor((s) => ({ ...s, open: o, risk: o ? s.risk : null }))}
        risk={riskEditor.risk}
        onSave={saveRisk}
      />
      <ObjectiveEditor
        open={objectiveEditor.open}
        onOpenChange={(o) => setObjectiveEditor((s) => ({ ...s, open: o, obj: o ? s.obj : null }))}
        objective={objectiveEditor.obj}
        assignableUsers={assignableUsers}
        onSave={saveObjective}
      />
      <WeeklyPrioritiesEditor
        open={weeklyEditorOpen}
        onOpenChange={setWeeklyEditorOpen}
        weekly={plan?.weekly_priorities}
        assignableUsers={assignableUsers}
        onSave={saveWeekly}
      />
    </div>
  );
}
